import addDays from 'date-fns/esm/addDays';
import isWithinInterval from 'date-fns/esm/isWithinInterval';
import orderBy from 'lodash-es/orderBy';
import { createSelector } from 'reselect';
import { ISalesCall, SalesCallTypes } from '.';
import { IState } from '..';
import { FilterTypes, getFilteredSalesDistricts, getFilters, ISalesCallFilters } from '../filters';

function getSalesCallFunc(
  state: IState,
  item: { businessEntityId: number; salesCallId: number; salesCallType: SalesCallTypes }
) {
  return {
    businessEntityId: item.businessEntityId,
    salesCall: state.salesCall.hash[item.salesCallId],
    salesCallType: item.salesCallType
  };
}

function newSalesCall(businessEntityId: number, salesCallType: SalesCallTypes): ISalesCall {
  return {
    accountNum: '',
    arrivalTimeUtc: '',
    brands: [],
    businessEntityId,
    contacts: [],
    createdAt: '',
    date: new Date(),
    departureTimeUtc: '',
    duration: 0,
    firstCallFail: false,
    nextVisitDate: null,
    nextVisitNotes: '',
    nextVisitObjectives: [],
    notes: '',
    objectives: [],
    salesCallId: -1,
    salesCallMethodId: -1,
    salesCallTypeId: salesCallType,
    salesDistrict: '',
    userName: ''
  };
}

export const getSalesCall = (state: IState, salesCallId: number) => state.salesCall.hash[salesCallId];

export const getSalesCallForm = createSelector(
  getSalesCallFunc,
  item => (item.salesCall ? item.salesCall : newSalesCall(item.businessEntityId, item.salesCallType))
);

export const getSalesCallMethod = (state: IState, salesCall: ISalesCall): string => {
  const {
    lookups: { salesCallMethods }
  } = state;
  const { salesCallMethodId } = salesCall;
  return salesCallMethods.hash[salesCallMethodId] ? salesCallMethods.hash[salesCallMethodId] : 'Unknown';
};

export const getSalesCallType = (state: IState, callTypeId: number): string => {
  const {
    lookups: { salesCallTypes }
  } = state;

  return salesCallTypes.hash[callTypeId] ? salesCallTypes.hash[callTypeId] : 'Unknown';
};

export const canEditSalesCall = (state: IState, salesCall: ISalesCall): boolean => {
  const { sales } = state.config;
  const salesCallEditDays = sales ? sales.salesCallEditDays : 0;
  // we multiply by negative 1 to make the day count a negative
  const today = new Date();
  const start = addDays(today, salesCallEditDays * -1);

  return isWithinInterval(new Date(salesCall.createdAt), { start, end: today });
};

export function salesCallsByDateLoaded(state: IState, date: Date | string): boolean {
  return state.salesCall.loadedDates.includes(date);
}

export function salesCallsByDateLoading(state: IState, date: Date | string): boolean {
  return state.salesCall.loadingDates.includes(date);
}

export function getSalesCallsByDate(state: IState, date: Date | string): ISalesCall[] {
  const dateArray = state.salesCall.salesCallsByDate[date.toString()];
  return dateArray ? dateArray.map(x => state.salesCall.hash[x]) : [];
}

export function getPagedSalesCalls(state: IState): ISalesCall[] {
  const {
    salesCall: { paged, hash }
  } = state;
  const filters = getFilters<ISalesCallFilters>(state, FilterTypes.salesCalls);
  const sds = getFilteredSalesDistricts(state, filters);
  return orderBy(
    paged.ids.map(x => hash[x]).filter(x => filterSalesCall(x, sds, filters)),
    [filters.sortBy, 'name'],
    ['desc', 'asc']
  );
}

function filterSalesCall(s: ISalesCall, salesDists: string[], filters: ISalesCallFilters): boolean {
  const nextVisitNotes = s.nextVisitNotes ? s.nextVisitNotes : '';
  return (
    salesDists.includes(s.salesDistrict) &&
    (!filters.sid || (Boolean(s.sid) && s.sid!.toLowerCase() === filters.sid.toLowerCase())) &&
    (filters.callTypes.length === 0 || filters.callTypes.includes(s.salesCallTypeId!)) &&
    (filters.callMethods.length === 0 || filters.callMethods.includes(s.salesCallMethodId!)) &&
    (!filters.searchTerm ||
      (s.notes.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        nextVisitNotes.toLowerCase().includes(filters.searchTerm.toLowerCase())))
  );
}
